import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { Home, Scissors, Sparkles, Loader2, Calendar, MapPin, Download, Share2, Music, Volume2 } from "lucide-react";

const fmtLaunchDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v; // legacy free-text value
  return d.toLocaleString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const FONT_FAMILIES = {
  modern: "",
  elegant: "Georgia, 'Palatino Linotype', 'Book Antiqua', serif",
  classic: "'Times New Roman', Times, serif",
  rounded: "'Trebuchet MS', Verdana, sans-serif",
};

export default function Launch() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("idle"); // idle | cutting | done
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/launch").then((r) => setData(r.data)).catch(() => setData({ enabled: false })).finally(() => setLoading(false));
  }, []);

  const areaParam = sp.get("area");
  const area = (areaParam && areaParam.trim()) || data?.default_area || "your city";
  const guestParam = sp.get("guest");
  const guest = (guestParam && guestParam.trim()) || data?.guest_default || "";
  const photo = data?.guest_photo || "";
  const photoSrc = photo ? (photo.startsWith("http") ? photo : `${process.env.REACT_APP_BACKEND_URL}${photo}`) : "";
  const eventDate = data?.event_date || "";
  const venue = data?.venue || "";
  const targetTs = eventDate ? new Date(eventDate).getTime() : NaN;
  const remaining = !isNaN(targetTs) ? targetTs - now : -1;
  const showCountdown = remaining > 0;
  const cd = {
    d: Math.floor(remaining / 86400000),
    h: Math.floor((remaining % 86400000) / 3600000),
    m: Math.floor((remaining % 3600000) / 60000),
    s: Math.floor((remaining % 60000) / 1000),
  };
  const cardFont = FONT_FAMILIES[data?.font_style] || "";
  const sizeZoom = { normal: 1, large: 1.12, xlarge: 1.25 }[data?.font_size] || 1;
  const headingStyle = cardFont ? { fontFamily: cardFont } : {};
  const cardRef = useRef(null);
  const wrapRef = useRef(null);
  const audioCtx = useRef(null);
  const musicTimer = useRef(null);
  const [musicOn, setMusicOn] = useState(false);

  const getAudio = () => {
    if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.current.state === "suspended") audioCtx.current.resume();
    return audioCtx.current;
  };
  const note = (ctx, freq, t, dur, vol = 0.08, type = "triangle") => {
    const o = ctx.createOscillator(); o.type = type; o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t + dur + 0.05);
  };
  const noiseBurst = (ctx, t, dur, vol, filterType, freq) => {
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) ch[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ch.length * 0.25));
    const s = ctx.createBufferSource(); s.buffer = buf;
    const f = ctx.createBiquadFilter(); f.type = filterType; f.frequency.value = freq; f.Q.value = 1.1;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur + 0.04);
    s.connect(f); f.connect(g); g.connect(ctx.destination); s.start(t);
  };
  const playSnip = () => {
    try {
      const ctx = getAudio(); const t = ctx.currentTime;
      noiseBurst(ctx, t, 0.04, 0.5, "highpass", 2600);
      noiseBurst(ctx, t + 0.13, 0.04, 0.5, "highpass", 2600);
    } catch (e) { /* audio unsupported */ }
  };
  const playApplause = () => {
    try {
      const ctx = getAudio(); const t0 = ctx.currentTime;
      for (let i = 0; i < 70; i++) {
        noiseBurst(ctx, t0 + Math.random() * 2.4, 0.03 + Math.random() * 0.05, 0.2 + Math.random() * 0.3, "bandpass", 900 + Math.random() * 2400);
      }
      [523.25, 659.25, 783.99, 1046.5].forEach((f) => note(ctx, f, t0 + 0.15, 2.4, 0.05, "sine")); // cheer chord
    } catch (e) { /* audio unsupported */ }
  };
  const toggleMusic = () => {
    if (musicOn) { clearInterval(musicTimer.current); musicTimer.current = null; setMusicOn(false); return; }
    try {
      const ctx = getAudio();
      const seq = [523.25, 659.25, 783.99, 1046.5, 783.99, 659.25];
      const loop = () => { const t0 = ctx.currentTime; seq.forEach((f, i) => note(ctx, f, t0 + i * 0.45, 0.6)); };
      loop();
      musicTimer.current = setInterval(loop, seq.length * 450);
      setMusicOn(true);
    } catch (e) { /* audio unsupported */ }
  };
  useEffect(() => () => clearInterval(musicTimer.current), []);

  // Auto-fit: scale the card down so the whole invitation fits any screen
  const [fitScale, setFitScale] = useState(1);
  useEffect(() => {
    const fit = () => {
      const box = wrapRef.current;
      if (!box) return;
      const prevZ = box.style.zoom;
      box.style.zoom = "1";
      const rect = box.getBoundingClientRect();
      box.style.zoom = prevZ;
      const availH = window.innerHeight - 24;
      const availW = window.innerWidth - 16;
      const s = Math.min(1, availH / (rect.height * sizeZoom), availW / (rect.width * sizeZoom));
      setFitScale(Math.max(0.4, s));
    };
    const t1 = setTimeout(fit, 80);
    const t2 = setTimeout(fit, 600); // re-fit after fonts/images settle
    window.addEventListener("resize", fit);
    window.addEventListener("load", fit);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", fit); window.removeEventListener("load", fit); };
  }, [data, phase, sizeZoom]);
  const src = (ph) => (ph ? (ph.startsWith("http") ? ph : `${process.env.REACT_APP_BACKEND_URL}${ph}`) : "");
  const adminGuests = data?.guests || [];
  const dignitaries = [];
  if (guestParam && guestParam.trim()) dignitaries.push({ name: guestParam.trim(), role: "Chief Guest", photo: adminGuests[0]?.photo || photo });
  adminGuests.forEach((g) => { if (!(guestParam && guestParam.trim() && g.role === "Chief Guest")) dignitaries.push(g); });
  if (!dignitaries.length && guest) dignitaries.push({ name: guest, role: "Chief Guest", photo });

  const download = async () => {
    if (!cardRef.current) return;
    const el = cardRef.current;
    const prevTransform = el.style.transform;
    const prevZoom = el.style.zoom;
    el.style.transform = "none";
    el.style.zoom = "1";
    try {
      const canvas = await html2canvas(el, { backgroundColor: "#fdfaf3", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `MyProperty-Launch-${area}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) { /* ignore */ } finally {
      el.style.transform = prevTransform;
      el.style.zoom = prevZoom;
    }
  };

  const launch = () => {
    if (phase !== "idle") return;
    playSnip();
    setPhase("cutting");
    setTimeout(() => { setPhase("done"); playApplause(); }, 2200);
  };

  const [redirectIn, setRedirectIn] = useState(null);
  useEffect(() => {
    if (phase !== "done") return;
    setRedirectIn(30);
    api.post("/launch/complete", { area }).catch(() => {});
    const iv = setInterval(() => setRedirectIn((s) => (s && s > 0 ? s - 1 : 0)), 1000);
    const to = setTimeout(() => navigate("/", { replace: true }), 30000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [phase]);

  const shareInvite = async () => {
    const url = window.location.href;
    const text = `You're invited to the Grand Launch of My Property in ${area}${eventDate ? ` on ${fmtLaunchDate(eventDate)}` : ""}${venue ? ` at ${venue}` : ""}!`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: "My Property — Grand Launch", text, url }); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Invite link copied — paste it anywhere to share");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = `${text}\n${url}`;
      ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Invite link copied — paste it anywhere to share"); } catch (e2) { toast.error(`Copy failed. Link: ${url}`); }
      document.body.removeChild(ta);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b1e17]">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (!data?.enabled) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0b1e17] text-center px-6" data-testid="launch-coming-soon">
        <div className="w-16 h-16 rounded-2xl bg-[#d4af37] flex items-center justify-center mb-6">
          <Home className="w-8 h-8 text-[#0b1e17]" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl text-white font-bold">Coming Soon</h1>
        <p className="text-[#d4af37]/80 mt-3 max-w-md">The grand launch of My Property is on its way. Please check back shortly.</p>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full flex items-center justify-center px-2 sm:px-4 relative overflow-hidden" style={{ background: "radial-gradient(1200px 600px at 50% -10%, #14392b 0%, #0b1e17 55%, #071410 100%)" }} data-testid="launch-page">
      {/* subtle gold grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#d4af37 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      {/* Confetti after cut */}
      {phase === "done" && (
        <div className="pointer-events-none absolute inset-0 z-30" data-testid="launch-confetti">
          {Array.from({ length: 40 }).map((_, i) => (
            <span key={i} className="launch-confetti" style={{
              left: `${Math.random() * 100}%`,
              background: ["#d4af37", "#1f7a5c", "#e63946", "#ffffff"][i % 4],
              animationDelay: `${Math.random() * 0.6}s`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }} />
          ))}
        </div>
      )}

      <div ref={wrapRef} className="relative w-full max-w-xl sm:max-w-2xl py-2" style={{ zoom: sizeZoom * fitScale }} data-testid="launch-fit-box">
        {/* Invitation card */}
        <div ref={cardRef} data-testid="launch-card" className="relative rounded-[28px] bg-[#fdfaf3] shadow-2xl border border-[#d4af37]/60 px-5 sm:px-10 py-5 sm:py-7 text-center" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.5), inset 0 0 0 6px rgba(212,175,55,0.15)", fontFamily: cardFont || undefined }}>
          {/* Ornate corners */}
          <span className="absolute top-4 left-4 w-10 h-10 border-t-2 border-l-2 border-[#d4af37] rounded-tl-xl" />
          <span className="absolute top-4 right-4 w-10 h-10 border-t-2 border-r-2 border-[#d4af37] rounded-tr-xl" />
          <span className="absolute bottom-4 left-4 w-10 h-10 border-b-2 border-l-2 border-[#d4af37] rounded-bl-xl" />
          <span className="absolute bottom-4 right-4 w-10 h-10 border-b-2 border-r-2 border-[#d4af37] rounded-br-xl" />

          <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:items-stretch text-center">
          <div className="flex flex-col items-center justify-center w-full" data-testid="launch-left-col">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center gap-2 w-full mx-auto" data-testid="launch-logo">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{ background: "linear-gradient(135deg,#1f7a5c,#0b3b2c)" }}>
              <Home className="w-6 h-6 text-white" />
            </div>
            <div className="font-heading font-extrabold text-xl tracking-tight text-[#0b3b2c]" style={headingStyle}>My Property</div>
            <div className="text-[11px] sm:text-xs tracking-[0.3em] text-[#d4af37] font-semibold uppercase">Grand Inauguration</div>
          </div>

          <div className="my-4 flex items-center justify-center gap-3 text-[#d4af37]">
            <span className="h-px w-14 bg-[#d4af37]/50" />
            <Sparkles className="w-4 h-4" />
            <span className="h-px w-14 bg-[#d4af37]/50" />
          </div>

          {dignitaries.length > 0 && (
            <div className="relative mb-3 sm:mb-0 rounded-xl overflow-hidden w-full" style={{ perspective: "900px" }} data-testid="launch-guest-curtain">
              <div className="py-2" data-testid="launch-guest">
                <div className="flex flex-wrap items-start justify-center gap-4">
                  {dignitaries.map((g, i) => (
                    <div key={i} className="flex flex-col items-center max-w-[190px]">
                      {src(g.photo) ? <img src={src(g.photo)} alt={g.name} crossOrigin="anonymous" className="block mx-auto w-auto h-auto max-w-[170px] max-h-28 sm:max-h-36 object-contain rounded-xl border-[3px] border-[#d4af37] shadow-lg mb-1.5 bg-white" data-testid={`launch-guest-photo-${i}`} /> : null}
                      <div className="text-[10px] tracking-[0.25em] text-[#d4af37] uppercase font-semibold">{g.role || "Guest of Honour"}</div>
                      <div className="font-heading text-base sm:text-lg font-bold text-[#0b3b2c] leading-tight" style={headingStyle} data-testid={`launch-guest-name-${i}`}>Sri {g.name}</div>
                      {g.info && <div className="text-xs sm:text-sm text-[#3a4a44] mt-0.5 leading-snug" data-testid={`launch-guest-info-${i}`}>{g.info}</div>}
                    </div>
                  ))}
                </div>
              </div>
              {phase !== "done" && (
                <div className="launch-door" data-testid="launch-door">
                  <span className="launch-door-text">Chief Guest<br /><span style={{ fontSize: "10px", letterSpacing: "0.2em", opacity: 0.85 }}>Revealed on launch</span></span>
                </div>
              )}
              {phase === "done" && <div className="launch-door open" data-testid="launch-door-open" />}
            </div>
          )}
          </div>
          <div className="flex flex-col items-center justify-center w-full" data-testid="launch-right-col">

          <p className="text-[#3a4a44] text-sm sm:text-base leading-relaxed font-medium text-center" data-testid="launch-invite-line">
            {dignitaries.length > 0 ? (dignitaries.length > 1 ? "are cordially invited to inaugurate" : "is cordially invited to inaugurate") : "You are cordially invited to the launch of"}
          </p>
          <p className="font-heading text-xl sm:text-2xl font-bold text-[#0b3b2c] mt-1" style={headingStyle}>My Property</p>
          <p className="text-[#3a4a44] text-sm mt-1">in</p>
          <p className="font-heading text-2xl sm:text-3xl font-extrabold mt-0.5" style={{ color: "#1f7a5c", ...headingStyle }} data-testid="launch-area">{area}</p>

          {(eventDate || venue) && (
            <div className="mt-3 space-y-1 text-[#3a4a44] text-xs sm:text-sm" data-testid="launch-details">
              {eventDate && <div className="flex items-center justify-center gap-2"><Calendar className="w-4 h-4 text-[#d4af37]" /> {fmtLaunchDate(eventDate)}</div>}
              {venue && <div className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4 text-[#d4af37]" /> {venue}</div>}
            </div>
          )}

          {/* Countdown to launch */}
          {showCountdown && (
            <div className="mt-4" data-testid="launch-countdown">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] font-semibold mb-2">Time to launch</div>
              <div className="flex items-center justify-center gap-2 sm:gap-2.5">
                {[["Days", cd.d], ["Hrs", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([label, v]) => (
                  <div key={label} className="w-12 rounded-lg border border-[#d4af37]/40 bg-white/70 py-1.5 shadow-sm" data-testid={`launch-cd-${label.toLowerCase()}`}>
                    <div className="font-heading text-lg font-extrabold text-[#0b3b2c] tabular-nums">{String(v).padStart(2, "0")}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#9a8a5a]">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ribbon + button */}
          <div className="relative mt-3 h-16 w-full flex items-center justify-center">
            {/* Ribbon halves */}
            {phase === "cutting" && <div className="launch-flash" data-testid="launch-flash" />}
            <div className={`launch-ribbon-half left ${phase !== "idle" ? "cut" : ""}`} />
            <div className={`launch-ribbon-half right ${phase !== "idle" ? "cut" : ""}`} />
            {/* Center bow / seal */}
            <div className={`launch-bow ${phase !== "idle" ? "cut" : ""}`}>
              <Scissors className="w-6 h-6 text-white" />
            </div>

            {phase === "done" && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-heading font-extrabold text-2xl text-[#1f7a5c] launch-open-text whitespace-nowrap" style={headingStyle}>Now Open!</span>
              </div>
            )}
          </div>

          {phase === "idle" ? (
            <button onClick={launch} data-testid="launch-cut-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-full px-7 py-3 font-semibold text-white text-base shadow-lg transition-transform hover:scale-[1.03] active:scale-95"
              style={{ background: "linear-gradient(135deg,#e63946,#b31e2c)" }}>
              <Scissors className="w-5 h-5" /> Cut the Ribbon
            </button>
          ) : phase === "cutting" ? (
            <p className="mt-4 text-[#3a4a44] flex items-center justify-center gap-2" data-testid="launch-status">
              <Loader2 className="w-4 h-4 animate-spin" /> Inaugurating...
            </p>
          ) : (
            <div className="mt-4 flex flex-col items-center gap-2.5" data-testid="launch-status">
              <p className="text-[#3a4a44] text-base font-medium">Welcome to My Property</p>
              <p className="text-sm text-[#9a8a5a]" data-testid="launch-redirect-count">Taking you to the website in {redirectIn ?? 30}s…</p>
              <button onClick={() => navigate("/", { replace: true })} data-testid="launch-enter-now"
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-semibold text-white shadow-lg transition-transform hover:scale-[1.03] active:scale-95"
                style={{ background: "linear-gradient(135deg,#1f7a5c,#0b3b2c)" }}>
                Enter Website Now
              </button>
            </div>
          )}
          </div>
          </div>

          <p className="mt-5 text-[11px] tracking-widest text-[#9a8a5a] uppercase">mypropertyas.com</p>
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2 sm:gap-3">
          <button onClick={shareInvite} data-testid="launch-share-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-white/10 text-white border border-[#d4af37]/40 hover:bg-white/20 transition-colors">
            <Share2 className="w-4 h-4" /> Share invitation
          </button>
          <button onClick={download} data-testid="launch-download-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-white/10 text-white border border-[#d4af37]/40 hover:bg-white/20 transition-colors">
            <Download className="w-4 h-4" /> Download invitation
          </button>
          <button onClick={toggleMusic} data-testid="launch-music-toggle" aria-label="Toggle background music"
            className="sm:fixed sm:bottom-5 sm:right-5 sm:z-40 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[#0b1e17] shadow-xl transition-transform hover:scale-105 active:scale-95"
            style={{ background: "linear-gradient(135deg,#d4af37,#b8860b)" }}>
            {musicOn ? <Volume2 className="w-4 h-4" /> : <Music className="w-4 h-4" />} {musicOn ? "Music on" : "Play music"}
          </button>
        </div>
      </div>
    </div>
  );
}
